/**
 * Lienzo del editor: la página en mm, con los elementos posicionados como los
 * dibuja el motor. Seleccionar (uno, varios con Shift o con un recuadro), mover
 * y redimensionar con el puntero; soltar campos y elementos de la paleta.
 *
 * ⚠️ El papel es blanco y el texto negro en los dos temas, como el PDF: es lo
 * único de la pantalla que no usa tokens, porque representa la hoja impresa.
 *
 * Fuente: Arimo, Tinos y Cousine tienen las mismas métricas que las Liberation
 * del PDF, así que un texto ocupa lo mismo en el lienzo y en la hoja.
 */
import { useEffect, useRef, useState, type CSSProperties, type DragEvent, type PointerEvent as PointerEventReact } from 'react'
import { useAssetUrl, type DatosDelElemento } from '@/modules/config/hooks/usePrintTemplates'
import {
  PX_POR_MM, acotar, acotarCorrimiento, ajustar, altoDeReferencias, altoDelLienzo, bordesDeColumnas, cajaDelGrupo, columnasQueSeVen,
  dentroDelRecuadro, desplazar, dimensiones, lineasDeIman, moverInicioDelDetalle, pegarBorde, pegarTramo, vaEnLaCopia,
  visibleEnVista, type Columna, type Copia, type Guias,
  zonaDelDetalle, TIPO_DE_ARRASTRE, type CampoArrastrado, type Caja, type Definicion, type Elemento, type Estilo, type Vista,
} from './geometria'

const FUENTES: Record<string, string> = {
  sans: 'Arimo, "Liberation Sans", Arial, sans-serif',
  serif: 'Tinos, "Liberation Serif", "Times New Roman", serif',
  mono: 'Cousine, "Liberation Mono", "Courier New", monospace',
}
const ID_FUENTES = 'fuentes-del-editor-de-plantillas'

/** Las fuentes se cargan al abrir el editor y no en toda la app. */
function useFuentesDelPapel() {
  useEffect(() => {
    if (document.getElementById(ID_FUENTES)) return
    const link = document.createElement('link')
    link.id = ID_FUENTES
    link.rel = 'stylesheet'
    link.href = 'https://fonts.googleapis.com/css2?family=Arimo:ital,wght@0,400;0,700;1,400&family=Cousine:wght@400;700&family=Tinos:wght@400;700&display=swap'
    document.head.appendChild(link)
  }, [])
}

type Manija = 'mover' | 'n' | 's' | 'e' | 'o' | 'ne' | 'no' | 'se' | 'so'

interface Arrastre {
  id: string
  manija: Manija
  inicioX: number
  inicioY: number
  original: Elemento
  /** Lo que se mueve: el elemento o, si era parte de la selección, todo el grupo. */
  grupo: Elemento[]
  /** Bordes y centros a los que pegarse, calculados al empezar: los demás no se mueven. */
  lineas: Guias
  movio: boolean
}

/** El recuadro de selección, en mm desde donde empezó. */
interface Recuadro {
  x0: number
  y0: number
  x1: number
  y1: number
  /** Con Shift o Ctrl suma a la selección en vez de reemplazarla. */
  sumar: boolean
}

const normalizar = (r: Recuadro): Caja => ({
  x: Math.min(r.x0, r.x1), y: Math.min(r.y0, r.y1), w: Math.abs(r.x1 - r.x0), h: Math.abs(r.y1 - r.y0),
})

function estiloCss(estilo: Estilo, esc: number, base: { family?: string; size_pt?: number }): CSSProperties {
  const pt = estilo.font_size_pt ?? base.size_pt ?? 9
  return {
    fontFamily: FUENTES[estilo.font_family ?? base.family ?? 'sans'],
    fontSize: `${(pt * 96) / 72 * (esc / PX_POR_MM)}px`,
    fontWeight: estilo.bold ? 700 : 400,
    fontStyle: estilo.italic ? 'italic' : 'normal',
    textTransform: estilo.uppercase ? 'uppercase' : 'none',
    textAlign: estilo.align ?? 'left',
    color: estilo.color || '#000000',
    background: estilo.background || 'transparent',
    border: estilo.border_mm ? `${estilo.border_mm * esc}px solid ${estilo.border_color || '#000000'}` : undefined,
    borderRadius: estilo.radius_mm ? `${estilo.radius_mm * esc}px` : undefined,
    padding: estilo.padding_mm ? `${estilo.padding_mm * esc}px` : undefined,
    lineHeight: estilo.line_height ?? 1.2,
  }
}

const VALIGN = { top: 'flex-start', middle: 'center', bottom: 'flex-end' } as const

const marcadorDe = (c: Columna) => (c.text !== undefined ? c.text : `{${c.path ?? ''}}`)

/**
 * Las columnas que se ven, en una grilla CSS que ocupa lo que ellas suman: si
 * una columna con condición no sale, la tabla se angosta (también sus líneas).
 */
const pistas = (columnas: Columna[], indices: number[], libre: number): CSSProperties => ({
  display: 'grid', gridTemplateColumns: indices.map(i => `${columnas[i].width_pct}fr`).join(' '),
  width: libre > 0 ? `${100 - libre}%` : undefined,
})

function Encabezados({ columnas, indices, libre, celda, estilo, borde }: {
  columnas: Columna[]; indices: number[]; libre: number; celda: CSSProperties; estilo?: CSSProperties; borde: string
}) {
  return (
    <div style={{ ...pistas(columnas, indices, libre), fontWeight: 700,
      borderBottom: borde, borderTop: estilo?.borderTop, ...estilo }}>
      {indices.map(i => <div key={i} style={{ ...celda, textAlign: columnas[i].align ?? 'left' }}>{columnas[i].header}</div>)}
    </div>
  )
}

function TablaDelLienzo({ el, datos, esc, base, verRutas, vista }: {
  el: Elemento; datos?: DatosDelElemento; esc: number; base: { family?: string; size_pt?: number }; verRutas: boolean
  vista: Vista
}) {
  const columnas = el.columns ?? []
  const { indices, libre } = columnasQueSeVen(columnas, datos?.columns_visible)
  const plantilla = pistas(columnas, indices, libre)
  const fuente = estiloCss({ ...(el.style ?? {}), font_size_pt: el.style?.font_size_pt ?? (el.type === 'references_table' ? 7 : base.size_pt) }, esc, base)
  const filas = verRutas ? [columnas.map(marcadorDe)] : datos?.rows ?? []
  const celda: CSSProperties = { padding: `${0.6 * esc}px ${esc}px`, overflow: 'hidden', whiteSpace: 'pre-line', textOverflow: 'ellipsis' }
  const grilla = el.grid || null
  const linea = grilla ? `${(grilla.width_mm ?? 0.2) * esc}px solid ${grilla.color ?? '#000000'}` : `${0.3 * esc}px solid #000000`
  // Las referencias van sobre el detalle solo en la primera página y si el ejemplo las trae.
  const refs = el.type === 'detail_table' && vista === 'first' ? el.references : undefined
  const filasDeRefs = refs ? (verRutas ? [refs.columns.map(marcadorDe)] : datos?.references ?? []) : []
  const verRefs = columnasQueSeVen(refs?.columns ?? [], datos?.references_visible)
  const altoRefs = refs ? altoDeReferencias(refs, filasDeRefs.length) : 0
  const altoFilaRefs = (refs?.row_h_mm ?? 4.5) * esc
  const celdaRefs: CSSProperties = { ...celda, padding: `0 ${esc}px`, whiteSpace: 'nowrap', height: altoFilaRefs, lineHeight: `${altoFilaRefs}px` }
  return (
    <div style={{ ...fuente, border: undefined, height: '100%', overflow: 'hidden', position: 'relative' }}>
      {altoRefs > 0 && refs && (
        <div style={{ height: altoRefs * esc, overflow: 'hidden' }}>
          {refs.title && <div style={{ ...celdaRefs, fontWeight: 700, padding: 0 }}>{refs.title}</div>}
          <Encabezados columnas={refs.columns} indices={verRefs.indices} libre={verRefs.libre} celda={celdaRefs} borde={linea}
            estilo={grilla ? { borderTop: linea, borderLeft: linea, borderRight: linea } : undefined} />
          {filasDeRefs.map((fila, i) => (
            <div key={i} style={{ ...pistas(refs.columns, verRefs.indices, verRefs.libre),
              borderBottom: grilla ? linea : undefined }}>
              {verRefs.indices.map(j => (
                <div key={j} style={{ ...celdaRefs, textAlign: refs.columns[j].align ?? 'left' }}>{fila[j] ?? ''}</div>
              ))}
            </div>
          ))}
        </div>
      )}
      <Encabezados columnas={columnas} indices={indices} libre={libre} celda={celda} borde={linea}
        estilo={grilla ? { borderTop: linea } : undefined} />
      {filas.map((fila, i) => (
        <div key={i} style={plantilla}>
          {indices.map(j => <div key={j} style={{ ...celda, textAlign: columnas[j].align ?? 'left' }}>{fila[j] ?? ''}</div>)}
        </div>
      ))}
      {!verRutas && (datos?.total_rows ?? 0) > filas.length && (
        <div style={{ ...celda, color: '#666666', fontStyle: 'italic' }}>… {(datos?.total_rows ?? 0) - filas.length} líneas más</div>
      )}
      {grilla && el.type === 'detail_table' && bordesDeColumnas(indices.map(i => columnas[i])).map((b, i) => (
        <div key={i} aria-hidden="true" style={{ position: 'absolute', top: altoRefs * esc, bottom: 0, left: `${b}%`,
          width: 0, borderLeft: linea, pointerEvents: 'none' }} />
      ))}
    </div>
  )
}

function ListaDelLienzo({ el, datos, esc, base, verRutas }: {
  el: Elemento; datos?: DatosDelElemento; esc: number; base: { family?: string; size_pt?: number }; verRutas: boolean
}) {
  const filas = verRutas || !datos?.rows ? (el.rows ?? []).map(f => [f.label, f.value]) : datos.rows
  const alto = (el.row_h_mm ?? 4.5) * esc
  const borde = el.grid ? `${(el.grid.width_mm ?? 0.3) * esc}px solid ${el.grid.color ?? '#000000'}` : undefined
  const celda = (estilo: Estilo | undefined): CSSProperties => ({
    ...estiloCss({ ...(el.style ?? {}), ...(estilo ?? {}) }, esc, base), background: undefined,
    height: alto, padding: `0 ${esc}px`, display: 'flex', alignItems: 'center', overflow: 'hidden', whiteSpace: 'nowrap',
    justifyContent: { left: 'flex-start', center: 'center', right: 'flex-end' }[estilo?.align ?? 'left'], border: borde,
  })
  return (
    <div style={{ height: '100%', overflow: 'hidden' }}>
      {filas.map(([rotulo, valor], i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: `${(el.label_w_mm ?? 0) * esc}px 1fr` }}>
          <div style={celda(el.label_style)}>{rotulo}</div>
          <div style={celda(el.value_style)}>{valor}</div>
        </div>
      ))}
    </div>
  )
}

function ImagenDelLienzo({ el }: { el: Elemento }) {
  const url = useAssetUrl(el.asset, 'content')
  if (!el.asset || !url) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
        color: '#888888', border: '1px dashed #888888' }}>
        {el.asset ? 'Cargando…' : 'Elegir la imagen'}
      </div>
    )
  }
  const ajuste = { contain: 'contain', cover: 'cover', stretch: 'fill' }[el.fit ?? 'contain'] as CSSProperties['objectFit']
  return <img src={url} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: ajuste, opacity: el.opacity ?? 1 }} />
}

function Contenido({ el, datos, esc, base, verRutas, vista }: {
  el: Elemento; datos?: DatosDelElemento; esc: number; base: { family?: string; size_pt?: number }; verRutas: boolean
  vista: Vista
}) {
  switch (el.type) {
    case 'text':
    case 'field': {
      const texto = verRutas || !datos ? (el.type === 'text' ? el.text ?? '' : `{${el.path ?? ''}}`) : datos.text ?? ''
      const estilo = el.style ?? {}
      return (
        <div style={{ ...estiloCss(estilo, esc, base), height: '100%', display: 'flex', flexDirection: 'column',
          justifyContent: VALIGN[estilo.valign ?? 'top'], whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {texto}
        </div>
      )
    }
    case 'rect':
      return <div style={{ ...estiloCss({ border_mm: 0.3, ...(el.style ?? {}) }, esc, base), height: '100%' }} />
    case 'detail_table':
    case 'references_table':
      return <TablaDelLienzo el={el} datos={datos} esc={esc} base={base} verRutas={verRutas} vista={vista} />
    case 'field_list':
      return <ListaDelLienzo el={el} datos={datos} esc={esc} base={base} verRutas={verRutas} />
    case 'image':
      return <ImagenDelLienzo el={el} />
    case 'barcode': {
      const conTexto = el.show_text !== false
      return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Las barras reales las dibuja el PDF; aquí basta su caja. */}
          <div style={{ flex: 1, background: 'repeating-linear-gradient(90deg, #000000 0 1px, #ffffff 1px 3px, #000000 3px 5px, #ffffff 5px 6px)' }} />
          {conTexto && (
            <div style={{ ...estiloCss({ font_size_pt: 7, align: 'center' }, esc, base), whiteSpace: 'nowrap', overflow: 'hidden' }}>
              {verRutas || !datos ? el.value : datos.text}
            </div>
          )}
        </div>
      )
    }
    case 'timbre': {
      const leyenda = el.legend === false ? 0 : 8
      return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* El PDF417 real lo dibuja el PDF; aquí basta su caja. */}
          <div style={{ height: `${((el.h_mm ?? 30) - leyenda) * esc}px`,
            background: 'repeating-linear-gradient(90deg, #000000 0 2px, #ffffff 2px 4px, #000000 4px 5px, #ffffff 5px 8px)' }} />
          {leyenda > 0 && (
            <div style={{ ...estiloCss({ font_size_pt: 7, align: 'center' }, esc, base), whiteSpace: 'pre-wrap' }}>
              {datos?.text ?? 'Timbre Electrónico SII'}
            </div>
          )}
        </div>
      )
    }
    case 'qr':
      return (
        <div title={datos?.text ?? el.value} style={{ height: '100%',
          background: 'repeating-conic-gradient(#000000 0 25%, #ffffff 0 50%) 0 0 / 20% 20%' }} />
      )
    default:
      return null
  }
}

/**
 * El PDF de fondo bajo los elementos: su miniatura estirada a la hoja. En las
 * páginas siguientes solo si el fondo va en todas.
 */
function FondoDelLienzo({ definicion, vista }: { definicion: Definicion; vista: Vista }) {
  const fondo = definicion.background
  const url = useAssetUrl(fondo?.asset, 'preview')
  if (!fondo || !url || (vista !== 'first' && fondo.pages !== 'all')) return null
  return <img src={url} alt="" aria-hidden="true" draggable={false}
    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }} />
}

const MANIJAS: Manija[] = ['no', 'n', 'ne', 'e', 'se', 's', 'so', 'o']
const POSICION_MANIJA: Record<Manija, CSSProperties> = {
  mover: {},
  no: { left: -4, top: -4, cursor: 'nwse-resize' }, n: { left: 'calc(50% - 4px)', top: -4, cursor: 'ns-resize' },
  ne: { right: -4, top: -4, cursor: 'nesw-resize' }, e: { right: -4, top: 'calc(50% - 4px)', cursor: 'ew-resize' },
  se: { right: -4, bottom: -4, cursor: 'nwse-resize' }, s: { left: 'calc(50% - 4px)', bottom: -4, cursor: 'ns-resize' },
  so: { left: -4, bottom: -4, cursor: 'nesw-resize' }, o: { left: -4, top: 'calc(50% - 4px)', cursor: 'ew-resize' },
}

/** Reglas en mm arriba y a la izquierda: marca cada 5 mm y número cada 10. Son interfaz, no papel. */
function Reglas({ ancho, alto, esc }: { ancho: number; alto: number; esc: number }) {
  const marcas = (largo: number) => Array.from({ length: Math.floor(largo / 5) + 1 }, (_, i) => i * 5)
  const cada = esc < 2 ? 20 : 10 // con poco zoom, los números se pisarían
  return (
    <>
      <div aria-hidden="true" className="absolute left-0 text-content-subtle" style={{ top: -16, height: 14, width: ancho * esc }}>
        {marcas(ancho).map(mm => (
          <span key={mm} className="absolute bottom-0 border-l border-divider-strong" style={{ left: mm * esc, height: mm % 10 ? 4 : 8 }}>
            {mm % cada === 0 && mm > 0 && <span className="absolute -top-2.5 -translate-x-1/2 text-[9px] leading-none">{mm}</span>}
          </span>
        ))}
      </div>
      <div aria-hidden="true" className="absolute top-0 text-content-subtle" style={{ left: -16, width: 14, height: alto * esc }}>
        {marcas(alto).map(mm => (
          <span key={mm} className="absolute right-0 border-t border-divider-strong" style={{ top: mm * esc, width: mm % 10 ? 4 : 8 }}>
            {mm % cada === 0 && mm > 0 && <span className="absolute -left-3 -translate-y-1/2 text-[9px] leading-none">{mm}</span>}
          </span>
        ))}
      </div>
    </>
  )
}

export function Lienzo({
  definicion, vista, copia, zoom, seleccion, datos, verRutas, onSeleccionar, onCambiar, onConfirmar,
  onSoltarCampo, onSoltarElemento,
}: {
  definicion: Definicion
  vista: Vista
  copia: Copia
  /** `'ajustar'`: la hoja ocupa el ancho disponible del lienzo. */
  zoom: number | 'ajustar'
  /** Los ids elegidos; los tiradores solo aparecen con uno. */
  seleccion: string[]
  datos: Record<string, DatosDelElemento>
  verRutas: boolean
  onSeleccionar: (ids: string[]) => void
  /** Cambio en curso (mientras se arrastra): no entra al historial. */
  onCambiar: (els: Elemento[]) => void
  /** Fin del arrastre: entra al historial. */
  onConfirmar: () => void
  onSoltarCampo: (campo: CampoArrastrado, x: number, y: number, sobre: string | null) => void
  onSoltarElemento: (tipo: string, x: number, y: number) => void
}) {
  useFuentesDelPapel()
  const marco = useRef<HTMLDivElement>(null)
  const pagina = useRef<HTMLDivElement>(null)
  const arrastre = useRef<Arrastre | null>(null)
  const [recuadro, setRecuadro] = useState<Recuadro | null>(null)
  const [anchoDisponible, setAnchoDisponible] = useState(0)
  const [guias, setGuias] = useState<Guias | null>(null)
  const { ancho, alto } = dimensiones(definicion.paper)

  useEffect(() => {
    if (!marco.current) return
    const observador = new ResizeObserver(([e]) => setAnchoDisponible(e.contentRect.width))
    observador.observe(marco.current)
    return () => observador.disconnect()
  }, [])
  // Ajustar al ancho, sin pasar del 150 % (un rollo de 58 mm se vería gigante).
  const zoomEfectivo = zoom === 'ajustar'
    ? Math.min(1.5, Math.max(0.3, anchoDisponible / (ancho * PX_POR_MM)) || 1)
    : zoom
  const esc = PX_POR_MM * zoomEfectivo
  const altoLienzo = altoDelLienzo(definicion)
  const base = definicion.font ?? {}

  const aMm = (clientX: number, clientY: number) => {
    const caja = pagina.current!.getBoundingClientRect()
    return { x: (clientX - caja.left) / esc, y: (clientY - caja.top) / esc }
  }

  const visibles = definicion.elements.filter(el => visibleEnVista(el, vista) && vaEnLaCopia(el, copia))

  const empezar = (e: PointerEventReact, el: Elemento, manija: Manija) => {
    if (e.button !== 0) return
    e.stopPropagation()
    if (manija === 'mover' && (e.shiftKey || e.ctrlKey || e.metaKey)) {
      // Shift o Ctrl: suma o quita el elemento, sin arrastrar.
      onSeleccionar(seleccion.includes(el.id) ? seleccion.filter(id => id !== el.id) : [...seleccion, el.id])
      return
    }
    const enGrupo = manija === 'mover' && seleccion.includes(el.id) && seleccion.length > 1
    if (!enGrupo) onSeleccionar([el.id])
    const grupo = enGrupo ? visibles.filter(v => seleccion.includes(v.id)) : [el]
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    arrastre.current = {
      id: el.id, manija, inicioX: e.clientX, inicioY: e.clientY, original: el, grupo,
      lineas: lineasDeIman(definicion, grupo.map(g => g.id), vista), movio: false,
    }
  }

  const empezarRecuadro = (e: PointerEventReact) => {
    if (e.button !== 0) return
    const sumar = e.shiftKey || e.ctrlKey || e.metaKey
    if (!sumar) onSeleccionar([])
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    const { x, y } = aMm(e.clientX, e.clientY)
    setRecuadro({ x0: x, y0: y, x1: x, y1: y, sumar })
  }

  const mover = (e: PointerEventReact) => {
    if (recuadro) {
      const { x, y } = aMm(e.clientX, e.clientY)
      setRecuadro({ ...recuadro, x1: x, y1: y })
      return
    }
    const a = arrastre.current
    if (!a) return
    a.movio = true
    // Alt: sin cuadrícula ni imán, al décimo de mm.
    const libre = e.altKey
    const dx = (e.clientX - a.inicioX) / esc
    const dy = (e.clientY - a.inicioY) / esc
    const o = a.original
    const fijar = (v: number) => (libre ? Math.round(v * 10) / 10 : ajustar(v))
    const guiasX: number[] = []
    const guiasY: number[] = []
    // El imán gana a la cuadrícula: se prueba sobre la posición sin redondear.
    const tramo = (inicio: number, largo: number, eje: 'x' | 'y') => {
      if (libre) return fijar(inicio)
      const r = pegarTramo(inicio, largo, a.lineas[eje])
      if (r.guia === null) return fijar(inicio)
      ;(eje === 'x' ? guiasX : guiasY).push(r.guia)
      return r.inicio
    }
    const borde = (valor: number, eje: 'x' | 'y') => {
      if (libre) return fijar(valor)
      const r = pegarBorde(valor, a.lineas[eje])
      if (r.guia === null) return fijar(valor)
      ;(eje === 'x' ? guiasX : guiasY).push(r.guia)
      return r.borde
    }
    if (a.manija === 'mover') {
      // Uno o varios: se mueve (y se pega) la caja que los envuelve.
      const caja = cajaDelGrupo(a.grupo, vista)
      const corrimiento = acotarCorrimiento(caja,
        tramo(caja.x + dx, caja.w, 'x') - caja.x, tramo(caja.y + dy, caja.h, 'y') - caja.y, ancho, alto)
      setGuias(guiasX.length || guiasY.length ? { x: guiasX, y: guiasY } : null)
      onCambiar(a.grupo.map(g => acotar(desplazar(g, corrimiento.dx, corrimiento.dy), ancho, alto)))
      return
    }
    let el: Elemento = { ...o }

    if (o.type === 'detail_table') {
      const zona = zonaDelDetalle(o, vista)
      if (a.manija.includes('n')) el = moverInicioDelDetalle(el, vista, borde(zona.top + dy, 'y'))
      if (a.manija.includes('s')) el = { ...el, bottom_mm: borde(zona.bottom + dy, 'y') }
      if (a.manija.includes('e')) el = { ...el, w_mm: borde(o.x_mm + o.w_mm + dx, 'x') - o.x_mm }
      if (a.manija.includes('o')) {
        const x = borde(o.x_mm + dx, 'x')
        el = { ...el, x_mm: x, w_mm: o.x_mm + o.w_mm - x }
      }
    } else {
      const h = o.h_mm ?? 5
      if (a.manija.includes('e')) el.w_mm = borde(o.x_mm + o.w_mm + dx, 'x') - o.x_mm
      if (a.manija.includes('o')) { el.x_mm = borde(o.x_mm + dx, 'x'); el.w_mm = o.x_mm + o.w_mm - el.x_mm }
      if (a.manija.includes('s')) el.h_mm = borde(o.y_mm + h + dy, 'y') - o.y_mm
      if (a.manija.includes('n')) { el.y_mm = borde(o.y_mm + dy, 'y'); el.h_mm = o.y_mm + h - el.y_mm }
    }
    setGuias(guiasX.length || guiasY.length ? { x: guiasX, y: guiasY } : null)
    onCambiar([acotar(el, ancho, alto)])
  }

  const terminar = () => {
    if (recuadro) {
      const caja = normalizar(recuadro)
      // Un clic sin arrastrar en la hoja solo limpia la selección.
      if (caja.w > 1 || caja.h > 1) {
        const dentro = dentroDelRecuadro(visibles, caja, vista)
        onSeleccionar(recuadro.sumar ? [...new Set([...seleccion, ...dentro])] : dentro)
      }
      setRecuadro(null)
      return
    }
    const a = arrastre.current
    if (!a) return
    arrastre.current = null
    setGuias(null)
    // Un clic sin mover sobre un elemento del grupo deja elegido solo ese.
    if (!a.movio && a.grupo.length > 1) onSeleccionar([a.id])
    onConfirmar()
  }

  const alSoltar = (e: DragEvent) => {
    e.preventDefault()
    const { x, y } = aMm(e.clientX, e.clientY)
    const sobre = (e.target as HTMLElement).closest('[data-elemento]')?.getAttribute('data-elemento') ?? null
    const campo = e.dataTransfer.getData(TIPO_DE_ARRASTRE.campo)
    if (campo) onSoltarCampo(JSON.parse(campo) as CampoArrastrado, ajustar(x), ajustar(y), sobre)
    const tipo = e.dataTransfer.getData(TIPO_DE_ARRASTRE.elemento)
    if (tipo) onSoltarElemento(tipo, ajustar(x), ajustar(y))
  }

  // Cuadrícula cada 5 mm, más marcada cada 10 mm.
  const cuadricula = `${5 * esc}px ${5 * esc}px`

  return (
    <div className="h-full overflow-auto bg-surface-muted rounded-xl border border-divider p-6">
      <div ref={marco} className="w-full" aria-hidden="true" />
      <div className="relative mx-auto" style={{ width: ancho * esc }}>
      <Reglas ancho={ancho} alto={altoLienzo} esc={esc} />
      <div
        ref={pagina}
        data-testid="lienzo-pagina"
        onPointerDown={empezarRecuadro}
        onPointerMove={mover}
        onPointerUp={terminar}
        onPointerCancel={terminar}
        onDragOver={e => {
          if ([...e.dataTransfer.types].some(t => t === TIPO_DE_ARRASTRE.campo || t === TIPO_DE_ARRASTRE.elemento)) {
            e.preventDefault()
            e.dataTransfer.dropEffect = 'copy'
          }
        }}
        onDrop={alSoltar}
        className="relative shadow-card select-none"
        style={{
          width: ancho * esc, height: altoLienzo * esc, background: '#ffffff', color: '#000000',
          backgroundImage: 'linear-gradient(rgba(0,0,0,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,.05) 1px, transparent 1px)',
          backgroundSize: cuadricula,
        }}
      >
        <FondoDelLienzo definicion={definicion} vista={vista} />
        {visibles.map(el => {
          const dato = datos[el.id]
          const esTabla = el.type === 'detail_table'
          const zona = esTabla ? zonaDelDetalle(el, vista) : null
          const top = zona ? zona.top : el.y_mm
          const h = zona ? zona.bottom - zona.top : el.h_mm ?? 5
          const elegido = seleccion.includes(el.id)
          const conManijas = elegido && seleccion.length === 1
          const oculto = dato && !dato.visible
          const caja: CSSProperties = {
            position: 'absolute', left: el.x_mm * esc, top: top * esc, width: el.w_mm * esc,
            height: el.type === 'line' && el.w_mm >= h ? 0 : Math.max(h * esc, 1),
            zIndex: el.z === 'back' ? 0 : esTabla ? 1 : 2,
            opacity: oculto ? 0.35 : 1,
            outline: elegido ? '2px solid rgb(var(--color-primary))'
              : dato?.error ? '1px solid rgb(var(--color-danger))'
                : esTabla ? '1px dashed rgba(0,0,0,.35)' : '1px dashed rgba(0,0,0,.12)',
            background: esTabla ? 'rgb(var(--color-primary) / 0.06)' : undefined,
            cursor: 'move',
          }
          if (el.type === 'line') {
            const grosor = `${(el.style?.border_mm ?? 0.3) * esc}px solid ${el.style?.border_color || '#000000'}`
            if (el.w_mm >= h) caja.borderTop = grosor
            else { caja.width = 0; caja.borderLeft = grosor }
          }
          return (
            <div key={el.id} data-elemento={el.id} style={caja}
              title={oculto ? 'No se imprime con este ejemplo: la condición no se cumple.' : dato?.error ?? el.id}
              onPointerDown={e => empezar(e, el, 'mover')}>
              {el.type !== 'line' && <Contenido el={el} datos={dato} esc={esc} base={base} verRutas={verRutas} vista={vista} />}
              {conManijas && MANIJAS.filter(m => el.type !== 'line' || ['e', 'o', 's', 'n'].includes(m)).map(m => (
                <span key={m} onPointerDown={e => empezar(e, el, m)} aria-hidden="true"
                  style={{ position: 'absolute', width: 8, height: 8, borderRadius: 2, zIndex: 3,
                    background: 'rgb(var(--color-primary))', border: '1px solid #ffffff', ...POSICION_MANIJA[m] }} />
              ))}
            </div>
          )
        })}
        {alto === null && (
          <div className="absolute left-0 right-0 text-center text-[10px]" style={{ bottom: 4, color: '#888888' }}>
            Alto automático: la hoja termina donde termina el contenido
          </div>
        )}
        {definicion.paper.two_per_sheet && (
          <div className="absolute left-0 right-0 text-center text-[10px]" style={{ bottom: -14, color: '#888888' }}>
            Se imprime dos veces por hoja: esta mitad y una copia igual debajo
          </div>
        )}
        {recuadro && (() => {
          const c = normalizar(recuadro)
          return (
            <div aria-hidden="true" data-testid="recuadro-de-seleccion" style={{ position: 'absolute', left: c.x * esc,
              top: c.y * esc, width: c.w * esc, height: c.h * esc, zIndex: 4, pointerEvents: 'none',
              border: '1px solid rgb(var(--color-primary))', background: 'rgb(var(--color-primary) / 0.08)' }} />
          )
        })()}
        {guias?.x.map((x, i) => (
          <div key={`gx${i}`} aria-hidden="true" style={{ position: 'absolute', top: 0, bottom: 0, left: x * esc, width: 0,
            borderLeft: '1px solid rgb(var(--color-danger))', zIndex: 4, pointerEvents: 'none' }} />
        ))}
        {guias?.y.map((y, i) => (
          <div key={`gy${i}`} aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: y * esc, height: 0,
            borderTop: '1px solid rgb(var(--color-danger))', zIndex: 4, pointerEvents: 'none' }} />
        ))}
      </div>
      </div>
    </div>
  )
}
